import { ChangeEvent, useState } from 'react';
import { Download, ExternalLink, FileUp, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { downloadCRMExport } from '@api/crm';
import { EmptyState } from '@components/common/EmptyState';
import { PageHeader } from '@components/common/PageHeader';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import {
  useLeadImportCommit,
  useLeadImportPreview,
  useLeads,
} from '@hooks/useCRM';
import { getApolloLeadUrls } from '@lib/apollo';
import { formatDate } from '@lib/format';
import type {
  CRMLeadImportPreviewResponse,
} from '@/types/api';

const IMPORT_FIELD_OPTIONS = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'company_name',
  'job_title',
  'city',
  'status',
  'priority',
  'follow_up_at',
  'why_target',
  'linkedin_url',
  'qualification_notes',
];

const LEAD_STATUS_OPTIONS = [
  { id: '', label: 'All statuses' },
  { id: 'new', label: 'New' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'called', label: 'Called' },
  { id: 'interested', label: 'Interested' },
  { id: 'demo_scheduled', label: 'Demo scheduled' },
  { id: 'converted', label: 'Converted' },
];

const IMPORT_MAPPING_OPTIONS = [
  { id: '', label: 'Ignore' },
  ...IMPORT_FIELD_OPTIONS.map((field) => ({ id: field, label: field })),
];

export function LeadsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importMapping, setImportMapping] = useState<Record<string, string>>({});
  const { data, isLoading } = useLeads(search || undefined, status || undefined);
  const previewImportMutation = useLeadImportPreview();
  const commitImportMutation = useLeadImportCommit();

  async function handlePreviewImport() {
    const preview = await previewImportMutation.mutateAsync({
      csv_text: importText,
      mapping: importMapping,
      source_type: 'import',
    });
    setImportMapping(preview.suggested_mapping);
  }

  async function handleCommitImport() {
    const response = await commitImportMutation.mutateAsync({
      csv_text: importText,
      mapping: importMapping,
      source_type: 'import',
    });
    if (response.leads[0]) {
      navigate(`/leads/${response.leads[0].id}`);
    }
    setImportOpen(false);
    setImportText('');
  }

  async function handleImportFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const text = await file.text();
    setImportText(text);
  }

  const preview = previewImportMutation.data;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Leads"
        subtitle="Inbound demo requests, sourced prospects, and manually tracked pipeline entries."
      />

      <SectionCard
       title="Lead Management"
        action={(
          <div className="toolbar-cluster">
            <label className="search-field">
              <Search size={16} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search leads" />
            </label>
            <SearchableSelect
              options={LEAD_STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
              placeholder="All statuses"
              clearable={false}
              aria-label="Lead status"
            />
            <button type="button" className="button button-secondary" onClick={() => navigate('/leads/new')}>
              <Plus size={16} />
              New Lead
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={() => {
                setImportOpen((current) => !current);
              }}
            >
              <FileUp size={16} />
              Import CSV
            </button>
            <button type="button" className="button button-secondary" onClick={() => downloadCRMExport('leads', 'csv')}>
              <Download size={16} />
              Export CSV
            </button>
          </div>
        )}
      >
      {importOpen ? (
        <div className="import-panel">
          <div className="subsection-header">
            <h3>Lead Import</h3>
            <p>Paste CSV text or upload a file, preview the mapping, then commit the rows.</p>
          </div>
          <div className="form-grid">
            <label className="form-field form-field-wide">
              <span>CSV Content</span>
              <textarea
                rows={8}
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder="first_name,last_name,email,company_name&#10;Morgan,Diaz,morgan@example.com,Prospect Co"
              />
            </label>
            <label className="form-field">
              <span>Upload file</span>
              <input type="file" accept=".csv,text/csv" onChange={handleImportFileChange} />
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="button button-secondary" onClick={handlePreviewImport} disabled={!importText.trim() || previewImportMutation.isPending}>
              {previewImportMutation.isPending ? 'Previewing…' : 'Preview Import'}
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={handleCommitImport}
              disabled={!preview?.importable_count || commitImportMutation.isPending}
            >
              {commitImportMutation.isPending ? 'Importing…' : 'Commit Import'}
            </button>
          </div>

          {preview ? (
            <ImportPreview
              preview={preview}
              mapping={importMapping}
              onMappingChange={(column, value) => setImportMapping((current) => ({ ...current, [column]: value }))}
            />
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <div className="loading-row">Loading leads…</div>
      ) : data?.items.length ? (
        <div className="table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Company</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Source</th>
                <th>Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((lead) => (
                <tr key={lead.id} className="clickable-row" onClick={() => navigate(`/leads/${lead.id}`)}>
                  <td>
                    <strong>{[lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.email || 'Unnamed lead'}</strong>
                    <span>
                      {lead.job_title || lead.email || 'No role yet'}
                      {lead.apollo_sequence_status ? ` • Apollo ${lead.apollo_sequence_status}` : ''}
                    </span>
                  </td>
                  <td>{lead.company_name || '—'}</td>
                  <td><span className="pill pill-neutral">{lead.status}</span></td>
                  <td><span className={`pill pill-${lead.priority}`}>{lead.priority}</span></td>
                  <td>{lead.source_type}</td>
                  <td>
                    <div className="table-cell-actions">
                      <span>{formatDate(lead.follow_up_at)}</span>
                      {getApolloLeadUrls(lead).contactUrl ? (
                        <button
                          type="button"
                          className="button button-secondary table-link-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            window.open(getApolloLeadUrls(lead).contactUrl!, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <ExternalLink size={14} />
                          Apollo
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No leads yet" body="Create a lead manually or use CSV import to seed the founder pipeline. Apollo prospecting now lives on its own page." />
      )}
      </SectionCard>
    </div>
  );
}

type ImportPreviewProps = {
  preview: CRMLeadImportPreviewResponse;
  mapping: Record<string, string>;
  onMappingChange: (column: string, value: string) => void;
};

function ImportPreview({ preview, mapping, onMappingChange }: ImportPreviewProps) {
  return (
    <div className="import-preview">
      <div className="subsection-header">
        <h3>Preview</h3>
        <p>
          {preview.importable_count} rows ready to import, {preview.skipped_count} skipped.
        </p>
      </div>

      <div className="form-grid">
        {preview.columns.map((column) => (
          <label key={column} className="form-field">
            <span>{column}</span>
            <SearchableSelect
              options={IMPORT_MAPPING_OPTIONS}
              value={mapping[column] ?? ''}
              onChange={(value) => onMappingChange(column, value)}
              placeholder="Ignore"
              clearable={false}
              aria-label={`Map ${column}`}
            />
          </label>
        ))}
      </div>

      <div className="table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Row</th>
              <th>Raw Values</th>
              <th>Mapped Values</th>
              <th>Warnings</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row) => (
              <tr key={row.row_number}>
                <td>{row.row_number}</td>
                <td><code className="compact-code">{JSON.stringify(row.values)}</code></td>
                <td><code className="compact-code">{JSON.stringify(row.mapped_values)}</code></td>
                <td>{row.warnings.join(' ') || 'Ready'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
